import { useCallback, useEffect, useRef, useState } from 'react';
import { assetsAPI } from '../api/assets';
import './BackgroundImagePickerModal.css';

const TAB_SITE = 'site';
const TAB_ALL = 'all';

export default function BackgroundImagePickerModal({
  open,
  websiteId,
  currentUrl,
  onClose,
  onSelect,
  title = 'Background image',
}) {
  const [activeTab, setActiveTab] = useState(TAB_SITE);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [selectedUrl, setSelectedUrl] = useState(currentUrl || '');
  const fileInputRef = useRef(null);

  const loadAssets = useCallback(async () => {
    if (!websiteId) return;
    setLoading(true);
    setError('');
    try {
      const scope = activeTab === TAB_SITE ? 'site' : 'all';
      const data = await assetsAPI.list(websiteId, scope);
      setAssets(Array.isArray(data.assets) ? data.assets : []);
    } catch (err) {
      setError(err.message || 'Failed to load images');
      setAssets([]);
    } finally {
      setLoading(false);
    }
  }, [websiteId, activeTab]);

  useEffect(() => {
    if (!open) return;
    setActiveTab(TAB_SITE);
    setSelectedUrl(currentUrl || '');
    setError('');
  }, [open, currentUrl]);

  useEffect(() => {
    if (!open || !websiteId) return;
    loadAssets();
  }, [open, websiteId, loadAssets]);

  const handleUpload = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file || !websiteId) return;
    setUploading(true);
    setError('');
    try {
      const scope = activeTab === TAB_SITE ? 'site' : 'global';
      const uploaded = await assetsAPI.upload(websiteId, file, scope);
      setSelectedUrl(uploaded.url);
      await loadAssets();
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleApply = () => {
    if (selectedUrl) {
      onSelect(selectedUrl);
    }
    onClose();
  };

  if (!open) return null;

  return (
    <div className="bg-image-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="bg-image-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bg-image-modal-title"
      >
        <div className="bg-image-modal-header">
          <h3 id="bg-image-modal-title">{title}</h3>
          <button type="button" className="bg-image-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="bg-image-modal-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === TAB_SITE}
            className={activeTab === TAB_SITE ? 'active' : ''}
            onClick={() => setActiveTab(TAB_SITE)}
          >
            This site
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === TAB_ALL}
            className={activeTab === TAB_ALL ? 'active' : ''}
            onClick={() => setActiveTab(TAB_ALL)}
          >
            All images
          </button>
        </div>

        <div className="bg-image-modal-toolbar">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*,audio/*,.pdf"
            className="bg-image-file-input"
            onChange={handleUpload}
          />
          <button
            type="button"
            className="bg-image-upload-btn"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? 'Uploading…' : 'Upload file'}
          </button>
          <span className="bg-image-upload-hint">
            {activeTab === TAB_SITE
              ? 'Saved to media/{this site}/ in blob storage'
              : 'Saved to media/ in blob storage'}
          </span>
        </div>

        {error && <p className="bg-image-modal-error">{error}</p>}

        <div className="bg-image-modal-body">
          {loading ? (
            <p className="bg-image-modal-status">Loading images…</p>
          ) : assets.length === 0 ? (
            <p className="bg-image-modal-status">No images yet. Upload one to get started.</p>
          ) : (
            <div className="bg-image-grid">
              {assets.map((asset) => (
                <button
                  key={asset.blobName || asset.url}
                  type="button"
                  className={`bg-image-grid-item${selectedUrl === asset.url ? ' selected' : ''}`}
                  onClick={() => setSelectedUrl(asset.url)}
                  title={asset.fileName || 'Image'}
                >
                  <img src={asset.url} alt="" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="bg-image-modal-footer">
          <button type="button" className="bg-image-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="bg-image-btn-primary"
            disabled={!selectedUrl}
            onClick={handleApply}
          >
            Use image
          </button>
        </div>
      </div>
    </div>
  );
}
